import { z } from 'zod';
import { createFileRoute, redirect, useChildMatches } from '@tanstack/react-router';
import { ArticlePath, createGuideLink, RouteName } from '@mews-commander/enums';
import { ReservationDetailContainer } from '@mews-commander/modules/src/modules/reservationModule';
import { Redirect } from '@mews-commander/ui';
import { MewsTeam, RouteType } from '@mews/enums';

export const Route = createFileRoute('/_authenticated/$currentEnterpriseId/Reservation/Detail/$reservationId')({
    validateSearch: z.object({
        groupId: z.string().optional(),
    }),
    beforeLoad: ({ context: { checkPrivilege } }) => {
        checkPrivilege('manageReservations');
    },
    loaderDeps: ({ search: { groupId } }) => ({ groupId }),
    loader: async ({ params: { reservationId }, deps: { groupId }, context: { ensureQueryData } }) => {
        if (groupId) {
            return { groupId };
        }

        const response = await ensureQueryData('reservations', 'getAll', { reservationIds: [reservationId] });
        throw redirect({
            to: '.',
            search: (s) => ({ ...s, groupId: response.reservations?.[0]?.groupId ?? '' }),
        });
    },
    component: Component,
    staticData: {
        excludeBreadcrumb: true,
        routeName: RouteName.ReservationDetail,
        owner: MewsTeam.Reservations,
        type: RouteType.Frontend,
    },
});

function Component() {
    const { reservationId, currentEnterpriseId } = Route.useParams();
    const { staticData } = Route.useMatch();
    const childMatches = useChildMatches();
    const { groupId } = Route.useLoaderData();

    if (staticData.routeName === RouteName.ReservationDetail && !childMatches.length) {
        return (
            <Redirect
                to="/$currentEnterpriseId/Reservation/Detail/$reservationId/Status"
                params={{ currentEnterpriseId, reservationId }}
                search
            />
        );
    }

    const guideLink = createGuideLink(ArticlePath.WhatReservationModuleWhatFeatures);

    return (
        <ReservationDetailContainer
            key={reservationId}
            reservationId={reservationId}
            guideLink={guideLink}
            groupId={groupId}
        />
    );
}
